import { useParams, Navigate, Link as RouterLink } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Alert,
  Stack,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemButton,
  Divider,
  Card,
  CardContent,
  Paper,
  Link,
} from "@mui/material";
import PageTitle from "../components/PageTitle";
import { useState, useEffect } from "react";
import { api, Game, GameVersion, User } from "../../shared/api/client";
import BoxCard from "../../shared/components/BoxCard";
import useUserId from "../../shared/hooks/useUserId";
import useIsSuperUser from "../../shared/hooks/useIsSuperUser";

type VersionSettings = Record<number, { stable: string; beta?: string }>;

export default function GameEdit() {
  const { name } = useParams();
  const [uid, uidLoading] = useUserId();
  const isSuperUser = useIsSuperUser();

  // Server data (source of truth)
  const [game, setGame] = useState<Game | null>(null);
  const [versions, setVersions] = useState<GameVersion[]>([]);
  const [testUsers, setTestUsers] = useState<User[]>([]);

  // Local editable state (changes tracked here until Save)
  const [localStableVersion, setLocalStableVersion] = useState<number>(0);
  const [localBetaVersion, setLocalBetaVersion] = useState<number | "">("");
  const [localVersionSettings, setLocalVersionSettings] = useState<VersionSettings>({});
  const [localTestUserIds, setLocalTestUserIds] = useState<string[]>([]);
  const [localVisibility, setLocalVisibility] = useState<'public' | 'unlisted' | 'private' | 'archived'>('public');

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Track if there are unsaved changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Add version state
  const [newVersionJson, setNewVersionJson] = useState("");
  const [addingVersion, setAddingVersion] = useState(false);

  // User search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [searching, setSearching] = useState(false);

  // Load game and versions only after we know the user
  useEffect(() => {
    if (!name || uidLoading) return;

    // Don't load data if user is not logged in
    if (!uid) {
      setLoading(false);
      return;
    }

    Promise.all([
      api.getGame(name),
      api.getVersions(name),
    ])
      .then(([gameData, versionsData]) => {
        setGame(gameData.game);
        setVersions(versionsData.versions);

        // Initialize local state from server data
        setLocalStableVersion(gameData.game.stableVersion);
        setLocalBetaVersion(gameData.game.betaVersion ?? "");
        setLocalVersionSettings(gameData.game.versions || {});
        setLocalTestUserIds(gameData.game.testUserIds || []);
        setLocalVisibility(gameData.game.visibility ?? 'public');

        // Load test user details if any
        if (gameData.game.testUserIds?.length) {
          api.getUsersByIds(gameData.game.testUserIds)
            .then((usersData) => setTestUsers(usersData.users))
            .catch(console.error);
        }

        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [name, uid, uidLoading]);

  // Check authorization
  const isAuthorized = game && (isSuperUser || game.ownerUserId === uid);

  if (!name) {
    throw new Error("No game name specified");
  }

  // Wait for auth to be determined
  if (uidLoading) {
    return (
      <Box>
        <PageTitle title="Loading..." />
      </Box>
    );
  }

  // Redirect if not logged in
  if (!uid) {
    return <Navigate to="/game" replace />;
  }

  if (loading) {
    return (
      <Box>
        <PageTitle title="Loading..." />
      </Box>
    );
  }

  if (game && !isAuthorized) {
    return <Navigate to="/game" replace />;
  }

  if (!game) {
    return (
      <Box>
        <PageTitle title="Game Not Found" />
        <Alert severity="error">Game not found</Alert>
      </Box>
    );
  }

  // Group versions by major version number
  const versionsByMajor = versions.reduce((acc, v) => {
    if (!acc[v.major]) acc[v.major] = [];
    acc[v.major].push(v);
    return acc;
  }, {} as Record<number, GameVersion[]>);

  const majorVersions = Object.keys(versionsByMajor).map(Number).sort((a, b) => b - a);

  // Handle local game-level channel changes
  const handleGameChannelChange = (channel: 'stable' | 'beta', value: number | "") => {
    setHasUnsavedChanges(true);
    if (channel === 'stable') {
      setLocalStableVersion(value === "" ? 0 : value);
    } else if (channel === 'beta') {
      setLocalBetaVersion(value);
    }
  };

  // Handle local per-major-version channel changes
  const handleVersionChannelChange = (major: number, channel: 'stable' | 'beta', versionId: string) => {
    setHasUnsavedChanges(true);
    setLocalVersionSettings((prev) => {
      const newSettings = { ...prev };
      if (!newSettings[major]) {
        newSettings[major] = { stable: '' };
      }
      if (channel === 'stable') {
        newSettings[major] = { ...newSettings[major], stable: versionId };
      } else if (channel === 'beta') {
        newSettings[major] = { ...newSettings[major], beta: versionId || undefined };
      }
      return newSettings;
    });
  };

  // Save all settings
  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await api.updateGame(name, {
        stableVersion: localStableVersion,
        betaVersion: localBetaVersion === "" ? null : localBetaVersion,
        versions: localVersionSettings,
        testUserIds: localTestUserIds,
        visibility: localVisibility,
      });
      setGame(result.game);
      setHasUnsavedChanges(false);
      setSuccess("Settings saved.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  // Add new version
  const handleAddVersion = async () => {
    setAddingVersion(true);
    setError(null);
    setSuccess(null);

    try {
      const parsed = JSON.parse(newVersionJson);
      const result = await api.createVersion(name, parsed);
      setVersions([...versions, result.version]);
      setNewVersionJson("");
      setSuccess(`Version ${result.version.version} added successfully.`);

      // Refresh game to get updated version settings (including auto-assigned channels)
      const gameData = await api.getGame(name);
      setGame(gameData.game);
      setLocalVersionSettings(gameData.game.versions || {});
      setLocalBetaVersion(gameData.game.betaVersion ?? "");
    } catch (err) {
      if (err instanceof SyntaxError) {
        setError("Invalid JSON format. Please check the syntax.");
      } else {
        setError(err instanceof Error ? err.message : "Failed to add version");
      }
    } finally {
      setAddingVersion(false);
    }
  };

  // Search users
  const handleSearchUsers = async () => {
    if (searchQuery.length < 2) return;

    setSearching(true);
    try {
      const result = await api.searchUsers(searchQuery);
      // Filter out users already in test users
      const filtered = result.users.filter(
        (u) => !localTestUserIds.includes(u.id)
      );
      setSearchResults(filtered);
    } catch (err) {
      console.error("Search failed:", err);
    } finally {
      setSearching(false);
    }
  };

  // Add test user (updates local state, doesn't save until user clicks Save)
  const handleAddTestUser = (user: User) => {
    setHasUnsavedChanges(true);
    setLocalTestUserIds([...localTestUserIds, user.id]);
    setTestUsers([...testUsers, user]);
    setSearchResults(searchResults.filter((u) => u.id !== user.id));
  };

  // Remove test user (updates local state, doesn't save until user clicks Save)
  const handleRemoveTestUser = (userId: string) => {
    setHasUnsavedChanges(true);
    setLocalTestUserIds(localTestUserIds.filter((id) => id !== userId));
    setTestUsers(testUsers.filter((u) => u.id !== userId));
  };

  return (
    <Box>
      <PageTitle title={`Edit: ${game.name}`} />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Visibility */}
      <BoxCard title="Visibility">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Controls who can see and access this game.
        </Typography>

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Visibility</InputLabel>
          <Select
            value={localVisibility}
            label="Visibility"
            onChange={(e) => {
              setLocalVisibility(e.target.value as typeof localVisibility);
              setHasUnsavedChanges(true);
            }}
          >
            <MenuItem value="public">Public</MenuItem>
            <MenuItem value="unlisted">Unlisted</MenuItem>
            <MenuItem value="private">Private</MenuItem>
            <MenuItem value="archived">Archived</MenuItem>
          </Select>
        </FormControl>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {localVisibility === 'public' && 'Visible to everyone in game lists and Discord libraries.'}
          {localVisibility === 'unlisted' && 'Accessible via direct link but hidden from game lists.'}
          {localVisibility === 'private' && 'Only visible to you, test users, and site admins.'}
          {localVisibility === 'archived' && 'Hidden from all lists. No new matches can be created.'}
        </Typography>
      </BoxCard>

      {/* Game-Level Channel Settings */}
      <BoxCard title="Game Channel Settings">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select which major version to use for each channel when creating new matches.
        </Typography>

        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Stable</InputLabel>
            <Select
              value={localStableVersion}
              label="Stable"
              onChange={(e) => handleGameChannelChange('stable', e.target.value as number)}
            >
              {majorVersions.map((major) => (
                <MenuItem key={major} value={major}>
                  Major {major}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Beta</InputLabel>
            <Select
              value={localBetaVersion}
              label="Beta"
              onChange={(e) => handleGameChannelChange('beta', e.target.value as number | "")}
            >
              <MenuItem value="">None</MenuItem>
              {majorVersions.map((major) => (
                <MenuItem key={major} value={major}>
                  Major {major}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

        </Stack>
      </BoxCard>

      {/* Versions by Major */}
      <BoxCard title="Versions">
        {majorVersions.length === 0 ? (
          <Typography color="text.secondary">No versions yet. Add one below.</Typography>
        ) : (
          majorVersions.map((major) => (
            <Card key={major} variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Major Version {major}
                </Typography>

                {/* Channel assignment for this major */}
                <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Stable</InputLabel>
                    <Select
                      value={localVersionSettings[major]?.stable ?? ''}
                      label="Stable"
                      onChange={(e) => handleVersionChannelChange(major, 'stable', e.target.value as string)}
                    >
                      {versionsByMajor[major].map((v) => (
                        <MenuItem key={v.version} value={v.version}>
                          {v.version}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Beta</InputLabel>
                    <Select
                      value={localVersionSettings[major]?.beta ?? ''}
                      label="Beta"
                      onChange={(e) => handleVersionChannelChange(major, 'beta', e.target.value as string)}
                    >
                      <MenuItem value="">None</MenuItem>
                      {versionsByMajor[major].map((v) => (
                        <MenuItem key={v.version} value={v.version}>
                          {v.version}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                </Stack>

                <Divider sx={{ my: 2 }} />

                {/* List versions in this major */}
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Available Versions
                </Typography>
                <Stack spacing={1}>
                  {versionsByMajor[major].map((v) => (
                    <Box
                      key={v.version}
                      sx={{
                        p: 1,
                        bgcolor: 'action.hover',
                        borderRadius: 1,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          <Link
                            component={RouterLink}
                            to={`/game/${name}/version/${v.version}`}
                            color="inherit"
                            underline="hover"
                          >
                            {v.version} - {v.name}
                          </Link>
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {v.minPlayers}-{v.maxPlayers} players
                        </Typography>
                      </Box>
                      {v.boxArt && (
                        <Box
                          component="img"
                          src={v.boxArt}
                          alt={v.name}
                          sx={{ height: 40, borderRadius: 0.5 }}
                        />
                      )}
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          ))
        )}
      </BoxCard>

      {/* Add Version */}
      <BoxCard title="Add Version">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Paste a version JSON document to add a new version to this game.
        </Typography>

        <TextField
          value={newVersionJson}
          onChange={(e) => setNewVersionJson(e.target.value)}
          multiline
          minRows={8}
          maxRows={15}
          fullWidth
          placeholder={`{
  "version": "1.0",
  "name": "Game Name",
  "major": 1,
  "description": "Version description",
  "maxPlayers": 4,
  "minPlayers": 2,
  "webEntry": "https://...",
  "nodeEntry": "https://...",
  "backgroundColor": "#000000",
  "foregroundColor": "#ffffff",
  "boxArt": "https://...",
  "screenshot": "https://..."
}`}
          sx={{ mb: 2, fontFamily: "monospace", fontSize: "0.875rem" }}
        />

        <Button
          variant="contained"
          onClick={handleAddVersion}
          disabled={addingVersion || !newVersionJson.trim()}
        >
          {addingVersion ? "Adding..." : "Add Version"}
        </Button>
      </BoxCard>

      {/* Test Users */}
      <BoxCard title="Test Users">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Test users can access beta versions of the game.
        </Typography>

        {/* Search */}
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearchUsers()}
            placeholder="Search by username..."
            size="small"
            sx={{ flex: 1 }}
          />
          <Button
            variant="outlined"
            onClick={handleSearchUsers}
            disabled={searching || searchQuery.length < 2}
          >
            {searching ? "Searching..." : "Search"}
          </Button>
        </Stack>

        {/* Search Results */}
        {searchResults.length > 0 && (
          <List dense sx={{ mb: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            {searchResults.map((user) => (
              <ListItem key={user.id} disablePadding>
                <ListItemButton onClick={() => handleAddTestUser(user)}>
                  <ListItemAvatar>
                    <Avatar src={user.avatarUrl} sx={{ width: 32, height: 32 }}>
                      {user.username?.[0]?.toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={user.displayName || user.username}
                    secondary={`@${user.username}`}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}

        {/* Current Test Users */}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Current Test Users
        </Typography>
        {testUsers.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No test users added yet.
          </Typography>
        ) : (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {testUsers.map((user) => (
              <Chip
                key={user.id}
                avatar={<Avatar src={user.avatarUrl}>{user.username?.[0]?.toUpperCase()}</Avatar>}
                label={user.displayName || user.username}
                onDelete={() => handleRemoveTestUser(user.id)}
                disabled={saving}
                sx={{ mb: 1 }}
              />
            ))}
          </Stack>
        )}
      </BoxCard>

      {/* Spacer for sticky save bar */}
      <Box sx={{ height: 80 }} />

      {/* Sticky Save Bar */}
      {hasUnsavedChanges && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            p: 2,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 2,
            zIndex: 1100,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            You have unsaved changes
          </Typography>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </Paper>
      )}
    </Box>
  );
}
